/**
 * ZoomFX LiveKit processor — background blur + portrait lighting, two
 * independent toggles. Replaces the old face_blur VideoTransformer, which
 * glitched (mask lag on motion, colour bleed at the cut-out edge).
 *
 * ZoomFX (src/lib/../../public/zoomfx) does MediaPipe selfie segmentation in a
 * Web Worker and composites on WebGL with a matched-pair render, so the mask
 * never "slides" off the person when they move. It works on a <video> →
 * <canvas>; we expose the canvas captureStream as the processor's
 * `processedTrack` so the SFU + recording egress get the already-processed
 * frames (same publish-before-SFU contract the blur processor had).
 *
 * All MediaPipe assets are served same-origin from /zoomfx/ (worker, wasm,
 * tflite models) — no CDN, which matters behind the RU firewall.
 */

import type { Track, TrackProcessor, ProcessorOptions } from "livekit-client";

const ZOOMFX_BASE = "/zoomfx";

export interface FxMode {
  blur: boolean;
  portrait: boolean;
}

export const FX_OFF: FxMode = { blur: false, portrait: false };

export function fxActive(mode: FxMode): boolean {
  return mode.blur || mode.portrait;
}

// Minimal shape of the vanilla ZoomFX class (public/zoomfx/zoomfx.js).
interface ZoomFXInstance {
  blur: boolean;
  portrait: boolean;
  start(): Promise<void>;
  stop(): void;
  outputStream(fps?: number): MediaStream;
}
interface ZoomFXCtor {
  new (opts: {
    video: HTMLVideoElement;
    canvas: HTMLCanvasElement;
    basePath?: string;
    mirror?: boolean;
    segMax?: number;
  }): ZoomFXInstance;
}

let zoomFxCtorPromise: Promise<ZoomFXCtor> | null = null;
function loadZoomFX(): Promise<ZoomFXCtor> {
  if (!zoomFxCtorPromise) {
    // Runtime import from public/ — keeps the worker + vendor pathing
    // self-consistent with basePath and out of the bundler's hands.
    zoomFxCtorPromise = import(/* @vite-ignore */ `${ZOOMFX_BASE}/zoomfx.js`).then(
      (m: { ZoomFX: ZoomFXCtor }) => m.ZoomFX,
    );
  }
  return zoomFxCtorPromise;
}

export class ZoomFXProcessor implements TrackProcessor<Track.Kind.Video> {
  name = "zoomfx";
  processedTrack?: MediaStreamTrack;

  private mode: FxMode;
  private fx?: ZoomFXInstance;
  private video?: HTMLVideoElement;
  private canvas?: HTMLCanvasElement;

  constructor(mode: FxMode) {
    this.mode = mode;
  }

  async init(opts: ProcessorOptions<Track.Kind.Video>): Promise<void> {
    const ZoomFX = await loadZoomFX();

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = new MediaStream([opts.track]);
    await video.play().catch(() => undefined);
    this.video = video;

    const canvas = document.createElement("canvas");
    this.canvas = canvas;

    // mirror=false: the published stream must NOT be mirrored — the remote
    // side and the recording see us un-mirrored. The local self-view is
    // mirrored separately via CSS, same as before.
    this.fx = new ZoomFX({ video, canvas, basePath: ZOOMFX_BASE, mirror: false });
    await this.fx.start();
    this.fx.blur = this.mode.blur;
    this.fx.portrait = this.mode.portrait;

    const stream = this.fx.outputStream(30);
    this.processedTrack = stream.getVideoTracks()[0];
  }

  async restart(opts: ProcessorOptions<Track.Kind.Video>): Promise<void> {
    await this.destroy();
    await this.init(opts);
  }

  async destroy(): Promise<void> {
    this.fx?.stop();
    this.processedTrack?.stop();
    this.processedTrack = undefined;
    if (this.video) {
      this.video.srcObject = null;
      this.video = undefined;
    }
    this.fx = undefined;
    this.canvas = undefined;
  }

  /** Flip effects live without tearing down the worker / wasm. */
  setMode(mode: FxMode): void {
    this.mode = mode;
    if (this.fx) {
      this.fx.blur = mode.blur;
      this.fx.portrait = mode.portrait;
    }
  }
}
